import { getAccessToken } from '../../../utils/auth'

/* global AbortController, TextDecoder */

// Opens the chat stream (Server-Sent Events) of the logged in user and keeps it open.
// EventSource can not send the Authorization header, so this reads a normal fetch response as a stream.
// It reconnects by itself (1s, 2s, 4s ... up to 15s) and tells the caller when it opened and closed,
// so the page can load what it missed in the meantime.

function apiBase () {
  let host = window.location.hostname
  host += host === 'localhost' ? ':8080' : ''
  return `${window.location.protocol}//${host}/api`
}

// one SSE frame -> { event, data, id } (comment lines such as ": ping" give null)
export function parseFrame (frame) {
  let event = 'message'
  let id = null
  const data = []
  frame.split('\n').forEach(line => {
    if (!line || line.charAt(0) === ':') return
    const colon = line.indexOf(':')
    const field = colon === -1 ? line : line.slice(0, colon)
    const value = colon === -1 ? '' : line.slice(colon + 1).replace(/^ /, '')
    if (field === 'event') event = value
    else if (field === 'id') id = value
    else if (field === 'data') data.push(value)
  })
  if (!data.length) return null
  return { event, id, data: data.join('\n') }
}

/**
 * @param {string} country the chat is per country
 * @param {number} generation the "new chat" number, 0 for the first chat
 * @param {Object} handlers { onOpen(), onMessage(message), onClose() }
 * @returns {{ close: Function }}
 */
export default function openChatStream (country, generation, handlers) {
  let stopped = false
  let controller = null
  let timer = null
  let retryMs = 1000

  function handleFrame (frame) {
    const parsed = parseFrame(frame)
    if (!parsed || parsed.event !== 'message') return
    try {
      handlers.onMessage && handlers.onMessage(JSON.parse(parsed.data))
    } catch (e) {
      // a broken frame is skipped, the next one still works
    }
  }

  async function connect () {
    controller = new AbortController()
    try {
      const res = await fetch(`${apiBase()}/chat/stream?country=${encodeURIComponent(country)}&generation=${generation || 0}`, {
        headers: { Authorization: `Bearer ${getAccessToken()}`, Accept: 'text/event-stream' },
        signal: controller.signal
      })
      if (!res.ok || !res.body) throw new Error(`stream answered ${res.status}`)
      retryMs = 1000
      handlers.onOpen && handlers.onOpen()
      const reader = res.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''
      for (;;) {
        const { value, done } = await reader.read()
        if (done) break
        buffer += decoder.decode(value, { stream: true })
        let end = buffer.indexOf('\n\n')
        while (end !== -1) {
          handleFrame(buffer.slice(0, end))
          buffer = buffer.slice(end + 2)
          end = buffer.indexOf('\n\n')
        }
      }
    } catch (e) {
      // closed on purpose or the connection dropped, handled below
    }
    if (stopped) return
    handlers.onClose && handlers.onClose()
    timer = setTimeout(connect, retryMs)
    retryMs = Math.min(retryMs * 2, 15000)
  }

  connect()

  return {
    close () {
      stopped = true
      clearTimeout(timer)
      if (controller) controller.abort()
    }
  }
}



// WEBPACK FOOTER //
// ./src/components/chat-simulator/chatStream.js